import type { PPAPersistFilter } from 'types/ppaViews';
import type {
    SimulatorAppliedFilters,
    SimulatorCategoryApplyFilter,
    SimulatorFilterCategory,
    SimulatorFilterOption,
} from './SimulatorFilterPanel.types';
import {
    EMPTY_SIMULATOR_SHOW_ONLY_STATE,
    SIMULATOR_APPLIED_FILTERS_VISIBLE_TAG_LIMIT,
    type SimulatorFilterScopeItem,
    type SimulatorShowOnlyState,
} from './simulatorFilter.helpers';
import { fetchSimulatorFilterAttributeNamesPreview } from './useSimulatorFilterAttributeNames';
import { isTrustedPrecomputedCategoryApplyFilter } from './simulatorFilterExcludeInclude.helpers';

/** Serializes applied simulator filters for create_view `persist_filter`. */
export function buildSimulatorPersistFilter(
    appliedFilters: SimulatorAppliedFilters,
    filterCategories: SimulatorFilterCategory[],
): PPAPersistFilter | undefined {
    const allCheckedAttribute: Record<string, string[]> = {};
    const allSelectedValueForAllAttribute: Record<string, number> = {};
    const applyFilterObject: Record<string, SimulatorCategoryApplyFilter> = {};

    filterCategories.forEach((category) => {
        const selectedIds = appliedFilters.categorySelections[category.key] ?? [];
        const isSelectAll = Boolean(appliedFilters.categorySelectAll?.[category.key]);
        const usedSelectAll = Boolean(appliedFilters.categoryUsedSelectAll?.[category.key]);
        if (!selectedIds.length && !isSelectAll) return;

        if (isSelectAll && selectedIds.length === 0) {
            const totalCount = appliedFilters.categoryTotalCounts?.[category.key] ?? 0;
            const previewIds = (appliedFilters.categorySelectAllPreviewOptions?.[category.key] ?? [])
                .slice(0, SIMULATOR_APPLIED_FILTERS_VISIBLE_TAG_LIMIT)
                .map((option) => option.id);
            allCheckedAttribute[category.key] = previewIds;
            allSelectedValueForAllAttribute[category.key] = totalCount;
            applyFilterObject[category.key] = { inclusion: [] };
            return;
        }

        allCheckedAttribute[category.key] = [...selectedIds];
        allSelectedValueForAllAttribute[category.key] = selectedIds.length;

        const precomputed = appliedFilters.categoryApplyFilters?.[category.key];
        if (
            isTrustedPrecomputedCategoryApplyFilter(precomputed, selectedIds, {
                isSelectAll,
                usedSelectAll,
            })
        ) {
            if (precomputed?.inclusion !== undefined) {
                applyFilterObject[category.key] = { inclusion: [...precomputed.inclusion] };
            } else if (precomputed?.exclusion !== undefined) {
                applyFilterObject[category.key] = { exclusion: [...precomputed.exclusion] };
            }
        } else {
            applyFilterObject[category.key] = { inclusion: [...selectedIds] };
        }
    });

    const hasCategoryFilters =
        Object.keys(allCheckedAttribute).length > 0 ||
        Object.values(appliedFilters.categorySelectAll ?? {}).some(Boolean);
    const hasShowOnly = appliedFilters.showOnly.edited || appliedFilters.showOnly.impacted;

    if (!hasCategoryFilters && !hasShowOnly) {
        return undefined;
    }

    const editedActionFilters =
        appliedFilters.showOnly.edited && appliedFilters.showOnly.editedFilters.length > 0
            ? appliedFilters.showOnly.editedFilters.map(({ id, input_value }) => ({ id, input_value }))
            : undefined;

    return {
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        showIsEditedRecords: appliedFilters.showOnly.edited,
        showIsImpactedRecords: appliedFilters.showOnly.impacted,
        ...(editedActionFilters ? { editedActionFilters } : {}),
    };
}

/** True when any category or show-only filters are currently applied. */
export function hasSimulatorAppliedFilters(
    appliedFilters: SimulatorAppliedFilters,
    filterCategories: SimulatorFilterCategory[],
): boolean {
    return buildSimulatorPersistFilter(appliedFilters, filterCategories) != null;
}

/** True when saved-view `persist_filter` contains restorable filter state (ignores `{}`). */
export function hasPersistFilterContent(
    persistFilter: PPAPersistFilter | undefined,
    filterCategories: SimulatorFilterCategory[],
): boolean {
    if (!persistFilter) {
        return false;
    }

    return hasSimulatorAppliedFilters(
        parseSimulatorAppliedFiltersFromPersistFilter(persistFilter, filterCategories),
        filterCategories,
    );
}

/** Compares applied filters using the same shape as saved-view `persist_filter`. */
export function areSimulatorAppliedFiltersEqual(
    left: SimulatorAppliedFilters,
    right: SimulatorAppliedFilters,
    filterCategories: SimulatorFilterCategory[],
): boolean {
    const leftPersist = buildSimulatorPersistFilter(left, filterCategories);
    const rightPersist = buildSimulatorPersistFilter(right, filterCategories);

    if (!leftPersist && !rightPersist) {
        return true;
    }
    if (!leftPersist || !rightPersist) {
        return false;
    }

    return JSON.stringify(leftPersist) === JSON.stringify(rightPersist);
}

function isPersistFilterSelectAllCategory(
    checkedIds: string[] | undefined,
    applyFilter: SimulatorCategoryApplyFilter | undefined,
): boolean {
    const emptyInclusion =
        applyFilter?.inclusion !== undefined &&
        applyFilter.inclusion.length === 0 &&
        (applyFilter.exclusion == null || applyFilter.exclusion.length === 0);

    if (emptyInclusion) {
        return true;
    }

    return Array.isArray(checkedIds) && checkedIds.length === 0 && applyFilter == null;
}

function assignPersistFilterCategoryApplyFilter(
    key: string,
    checkedIds: string[] | undefined,
    applyFilter: SimulatorCategoryApplyFilter | undefined,
    categoryApplyFilters: Record<string, SimulatorCategoryApplyFilter>,
) {
    if (applyFilter?.inclusion !== undefined && applyFilter.inclusion.length > 0) {
        categoryApplyFilters[key] = { inclusion: [...applyFilter.inclusion] };
        return;
    }
    if (applyFilter?.exclusion !== undefined && applyFilter.exclusion.length > 0) {
        categoryApplyFilters[key] = { exclusion: [...applyFilter.exclusion] };
        return;
    }
    if (Array.isArray(checkedIds) && checkedIds.length > 0) {
        categoryApplyFilters[key] = { inclusion: [...checkedIds] };
    }
}

function parsePersistFilterSelectAllCategory(
    key: string,
    persistFilter: PPAPersistFilter,
    checkedIds: string[] | undefined,
): {
    categorySelections: Record<string, string[]>;
    categorySelectAll: Record<string, boolean>;
    categoryApplyFilters: Record<string, SimulatorCategoryApplyFilter>;
    categoryTotalCounts: Record<string, number>;
    categorySelectAllPreviewOptions: Record<string, SimulatorFilterOption[]>;
} {
    const categorySelections: Record<string, string[]> = { [key]: [] };
    const categorySelectAll: Record<string, boolean> = { [key]: true };
    const categoryApplyFilters: Record<string, SimulatorCategoryApplyFilter> = {
        [key]: { inclusion: [] },
    };
    const categoryTotalCounts: Record<string, number> = {};
    const categorySelectAllPreviewOptions: Record<string, SimulatorFilterOption[]> = {};

    const totalCount = persistFilter.allSelectedValueForAllAttribute?.[key];
    if (totalCount != null) {
        categoryTotalCounts[key] = totalCount;
    }
    if (Array.isArray(checkedIds) && checkedIds.length > 0) {
        categorySelectAllPreviewOptions[key] = checkedIds.map((id) => ({ id, label: id }));
    }

    return {
        categorySelections,
        categorySelectAll,
        categoryApplyFilters,
        categoryTotalCounts,
        categorySelectAllPreviewOptions,
    };
}

function parsePersistFilterCategory(
    key: string,
    persistFilter: PPAPersistFilter,
): {
    categorySelections: Record<string, string[]>;
    categorySelectAll: Record<string, boolean>;
    categoryApplyFilters: Record<string, SimulatorCategoryApplyFilter>;
    categoryTotalCounts: Record<string, number>;
    categorySelectAllPreviewOptions: Record<string, SimulatorFilterOption[]>;
} {
    const categorySelections: Record<string, string[]> = {};
    const categorySelectAll: Record<string, boolean> = {};
    const categoryApplyFilters: Record<string, SimulatorCategoryApplyFilter> = {};
    const categoryTotalCounts: Record<string, number> = {};
    const categorySelectAllPreviewOptions: Record<string, SimulatorFilterOption[]> = {};

    const checkedIds = persistFilter.allCheckedAttribute?.[key];
    const applyFilter = persistFilter.applyFilterObject?.[key];

    if (checkedIds === undefined && applyFilter === undefined) {
        return {
            categorySelections,
            categorySelectAll,
            categoryApplyFilters,
            categoryTotalCounts,
            categorySelectAllPreviewOptions,
        };
    }

    if (isPersistFilterSelectAllCategory(checkedIds, applyFilter)) {
        return parsePersistFilterSelectAllCategory(key, persistFilter, checkedIds);
    }

    if (Array.isArray(checkedIds) && checkedIds.length > 0) {
        categorySelections[key] = [...checkedIds];
    }

    assignPersistFilterCategoryApplyFilter(key, checkedIds, applyFilter, categoryApplyFilters);

    const selectedCount = persistFilter.allSelectedValueForAllAttribute?.[key];
    if (selectedCount != null) {
        categoryTotalCounts[key] = selectedCount;
    }

    return {
        categorySelections,
        categorySelectAll,
        categoryApplyFilters,
        categoryTotalCounts,
        categorySelectAllPreviewOptions,
    };
}

/** Restores filter-panel state from create_view / get_view_list `persist_filter`. */
export function parseSimulatorAppliedFiltersFromPersistFilter(
    persistFilter: PPAPersistFilter | undefined,
    filterCategories: SimulatorFilterCategory[] = [],
): SimulatorAppliedFilters {
    if (!persistFilter) {
        return {
            categorySelections: {},
            showOnly: EMPTY_SIMULATOR_SHOW_ONLY_STATE,
        };
    }

    const categoryKeys = new Set<string>([
        ...Object.keys(persistFilter.allCheckedAttribute ?? {}),
        ...Object.keys(persistFilter.applyFilterObject ?? {}),
        ...filterCategories.map((category) => category.key),
    ]);

    const categorySelections: Record<string, string[]> = {};
    const categorySelectAll: Record<string, boolean> = {};
    const categoryApplyFilters: Record<string, SimulatorCategoryApplyFilter> = {};
    const categoryTotalCounts: Record<string, number> = {};
    const categorySelectAllPreviewOptions: Record<string, SimulatorFilterOption[]> = {};

    categoryKeys.forEach((key) => {
        const parsed = parsePersistFilterCategory(key, persistFilter);
        Object.assign(categorySelections, parsed.categorySelections);
        Object.assign(categorySelectAll, parsed.categorySelectAll);
        Object.assign(categoryApplyFilters, parsed.categoryApplyFilters);
        Object.assign(categoryTotalCounts, parsed.categoryTotalCounts);
        Object.assign(categorySelectAllPreviewOptions, parsed.categorySelectAllPreviewOptions);
    });

    const showOnly: SimulatorShowOnlyState = {
        edited: persistFilter.showIsEditedRecords ?? false,
        impacted: persistFilter.showIsImpactedRecords ?? false,
        editedFilters:
            persistFilter.editedActionFilters?.map(({ id, input_value }) => ({ id, input_value })) ?? [],
    };

    return {
        categorySelections,
        ...(Object.keys(categorySelectAll).length > 0 ? { categorySelectAll } : {}),
        ...(Object.keys(categoryApplyFilters).length > 0 ? { categoryApplyFilters } : {}),
        ...(Object.keys(categoryTotalCounts).length > 0 ? { categoryTotalCounts } : {}),
        ...(Object.keys(categorySelectAllPreviewOptions).length > 0
            ? { categorySelectAllPreviewOptions }
            : {}),
        showOnly,
    };
}

/** Loads attribute-name labels for select-all categories restored from persist_filter. */
export async function hydrateSimulatorSelectAllPreviewOptions(
    filters: SimulatorAppliedFilters,
    filterCategories: SimulatorFilterCategory[],
    appliedScope?: SimulatorFilterScopeItem[],
): Promise<SimulatorAppliedFilters> {
    const selectAllKeys = Object.entries(filters.categorySelectAll ?? {})
        .filter(([, isSelectAll]) => isSelectAll)
        .map(([key]) => key);

    if (selectAllKeys.length === 0) {
        return filters;
    }

    const previewOptions = { ...(filters.categorySelectAllPreviewOptions ?? {}) };

    await Promise.all(
        selectAllKeys.map(async (categoryKey) => {
            const category = filterCategories.find((item) => item.key === categoryKey);
            if (!category) return;

            if ((filters.categorySelectAllPreviewOptions?.[categoryKey]?.length ?? 0) > 0) {
                return;
            }

            const options = await fetchSimulatorFilterAttributeNamesPreview({
                category,
                appliedScope,
                showOnly: filters.showOnly,
            });
            if (options.length > 0) {
                previewOptions[categoryKey] = options;
            }
        }),
    );

    return {
        ...filters,
        categorySelectAllPreviewOptions: previewOptions,
    };
}
